// ═══════════════════════════════════════════════════════════
// 솟다 모의투자 — 순수 계산 엔진 (DB·네트워크 무관, TDD 대상)
// design doc: docs/specs/2026-06-06-sotda-investment-sim-game-design.md §14
//
// 단위 규약:
//   - BULL = 정수 (number, 2^53까지 안전 — 수억 BULL 무관)
//   - 평단·체결가(price)·평가가(curPrice) = 현지 통화 (USD 종목은 USD)
//   - fxRate = 체결 시 원화 환율 (KRW 종목=1, USD 종목=원/달러). BULL 환산용
//   - 음수 방지·잔액/수량 검증은 호출부(RPC)와 이중. 여기선 throw로 신호
// ═══════════════════════════════════════════════════════════

/** BULL은 정수 — 환산 결과를 반올림 */
export const toBull = (n: number): number => Math.round(n);

/** 체결 BULL 금액 = 수량 × 체결가 × 환율 */
export function tradeAmountBull(qty: number, price: number, fxRate: number): number {
  return toBull(qty * price * fxRate);
}

/** 포지션 평가액(BULL) = 수량 × 현재가 × 환율 */
export function positionValueBull(qty: number, curPrice: number, fxRate: number): number {
  return toBull(qty * curPrice * fxRate);
}

/** 평가손익(BULL) = (현재가 − 평단) × 수량 × 환율 */
export function unrealizedPnlBull(
  qty: number, avgPrice: number, curPrice: number, fxRate: number,
): number {
  return toBull((curPrice - avgPrice) * qty * fxRate);
}

/** 추가 매수 후 평단 합산 (현지 통화 기준). 수량 0이면 평단 0 */
export function addToPosition(
  prevQty: number, prevAvg: number, addQty: number, addPrice: number,
): { qty: number; avgPrice: number } {
  const qty = prevQty + addQty;
  const avgPrice = qty === 0 ? 0 : (prevQty * prevAvg + addQty * addPrice) / qty;
  return { qty, avgPrice };
}

/** 매도 시 실현손익(BULL)과 매도대금(BULL). 평단 기준 */
export function sellFromPosition(
  avgPrice: number, sellQty: number, sellPrice: number, fxRate: number,
): { realizedPnlBull: number; proceedsBull: number } {
  const realizedPnlBull = toBull((sellPrice - avgPrice) * sellQty * fxRate);
  const proceedsBull = toBull(sellQty * sellPrice * fxRate);
  return { realizedPnlBull, proceedsBull };
}

/**
 * 매수 적용 — 잔액 검증 + 평단 합산.
 * cost = 체결금액 + 수수료. cash 부족 시 throw (RPC와 이중 방어)
 */
export function applyBuy(
  cashBalance: number,
  prevQty: number, prevAvg: number,
  qty: number, price: number, fxRate: number,
  feeBull = 0,
): { cashAfter: number; qty: number; avgPrice: number; costBull: number } {
  if (qty <= 0) throw new Error('applyBuy: qty must be positive');
  const costBull = tradeAmountBull(qty, price, fxRate) + feeBull;
  if (costBull > cashBalance) {
    throw new Error(`applyBuy: insufficient cash (have ${cashBalance}, need ${costBull})`);
  }
  const pos = addToPosition(prevQty, prevAvg, qty, price);
  return { cashAfter: cashBalance - costBull, qty: pos.qty, avgPrice: pos.avgPrice, costBull };
}

/**
 * 매도 적용 — 보유 수량 검증 + 실현손익.
 * net = 매도대금 − 수수료. 보유 초과 매도 시 throw
 */
export function applySell(
  cashBalance: number,
  prevQty: number, avgPrice: number,
  qty: number, price: number, fxRate: number,
  feeBull = 0,
): { cashAfter: number; remainingQty: number; realizedPnlBull: number; proceedsBull: number } {
  if (qty <= 0) throw new Error('applySell: qty must be positive');
  if (qty > prevQty) throw new Error(`applySell: insufficient qty (have ${prevQty}, sell ${qty})`);
  const { realizedPnlBull, proceedsBull } = sellFromPosition(avgPrice, qty, price, fxRate);
  const net = proceedsBull - feeBull;
  return {
    cashAfter: cashBalance + net,
    remainingQty: prevQty - qty,
    realizedPnlBull,
    proceedsBull,
  };
}

/** 수익률 % = (현재 − 시작) / 시작 × 100. 시작 0이면 0 */
export function returnPct(startBull: number, currentBull: number): number {
  if (startBull === 0) return 0;
  return ((currentBull - startBull) / startBull) * 100;
}

/**
 * 누적 알파(%p) = 포트 수익률 − 벤치마크 수익률.
 * 단순 수익률 아닌 "시장 이긴 정도" = 운 보정·공정 랭킹 (design doc §5)
 */
export function computeAlpha(portfolioReturnPct: number, benchmarkReturnPct: number): number {
  return portfolioReturnPct - benchmarkReturnPct;
}

export type EyeGrade = { key: string; label: string };

/**
 * 안목 등급 — 누적 알파 → 등급 (design doc §5)
 * 🐣초보 → 👀눈썰미 → 🎯고수 → 🦅대가 → 👑전설
 * 임계값(%p): 2 / 7 / 15 / 30 (MVP 가정 — 운영 데이터로 보정)
 */
export function eyeGrade(cumAlpha: number): EyeGrade {
  // label 이모지 제거(2026-06-14) — hero 배지가 ★ SVG + label이라 ★+이모지 중복.
  //   등급 한글명은 솟다 안목 체계(도메인)라 유지, 이모지만 제거. 사용처: SimScreen 배지·PortfolioAnalysis.
  if (cumAlpha >= 30) return { key: 'legend', label: '전설' };
  if (cumAlpha >= 15) return { key: 'master', label: '대가' };
  if (cumAlpha >= 7) return { key: 'expert', label: '고수' };
  if (cumAlpha >= 2) return { key: 'sharp', label: '눈썰미' };
  return { key: 'rookie', label: '초보' };
}

/**
 * 안목 등급 단계 표 — 홈 ★배지 설명 모달용(단일 출처). eyeGrade 임계값과 동기.
 * P2 §13: 배지 탭 시 "안목 등급이 뭔지·어떻게 오르는지" 설명. min = 누적 알파(%p) 하한.
 */
export const EYE_GRADE_TIERS: { key: string; label: string; min: number }[] = [
  { key: 'rookie', label: '초보', min: 0 },
  { key: 'sharp', label: '눈썰미', min: 2 },
  { key: 'expert', label: '고수', min: 7 },
  { key: 'master', label: '대가', min: 15 },
  { key: 'legend', label: '전설', min: 30 },
];

// ────────────────────────────────────────
// 호흡2 — 유저 백분위 벤치마크 (3단계 적응형 안목 등급)
// 결정 A(2026-06-26): 백분위 = 유저 순수 수익률(pnl_pct) 줄세우기.
//   코스피/나스닥 벤치 무관 → 국장·미장 유저 혼재 논쟁 소멸.
// design doc: docs/specs/2026-06-26-sotda-benchmark-percentile-backend-design.md §2
// ────────────────────────────────────────

/** 단계: 씨앗(절대 수익률) / 성장(혼합) / 성숙(순수 백분위). 활성유저수 or 수동 오버라이드로 결정(서버 RPC). */
export type BenchmarkStage = 'seed' | 'grow' | 'mature';

/**
 * 백분위 → 안목 등급 (성숙 단계). percentile = 상위 N%(작을수록 상위).
 * 상위 5/15/35/60% 경계 = 전설/대가/고수/눈썰미/초보 (대략 정규분포 가정·운영 보정).
 */
export function eyeGradeByPercentile(percentile: number): EyeGrade {
  if (percentile <= 5) return { key: 'legend', label: '전설' };
  if (percentile <= 15) return { key: 'master', label: '대가' };
  if (percentile <= 35) return { key: 'expert', label: '고수' };
  if (percentile <= 60) return { key: 'sharp', label: '눈썰미' };
  return { key: 'rookie', label: '초보' };
}

const GRADE_RANK: Record<string, number> = { rookie: 0, sharp: 1, expert: 2, master: 3, legend: 4 };

export type ResolvedEyeGrade = {
  grade: EyeGrade;
  stage: BenchmarkStage;
  basis: string;            // 등급 산정 기준 문구(모달·배지 부가설명)
  percentile: number | null;
  /** 이번 등급이 실제로 '유저 백분위'로 정해졌는가. grow 단계는 절대·백분위 중 높은 쪽을 쓰므로
   *  단계(stage)만으로는 알 수 없다. 화면 안내 문구가 이 값으로 갈린다(2026-09-09). */
  byPercentile: boolean;
};

/**
 * 3단계 적응형 안목 등급.
 * - seed   (<30 활성): 절대 수익률 임계값(벤치마크 무관·현 동작과 동일)
 * - grow   (30~199):   절대·백분위 중 높은 단계(둘 다 인정)
 * - mature (200+):     순수 유저 백분위
 * percentile 없으면(활성0·내 스냅샷 없음) 절대 수익률로 폴백 → 씨앗 단계 표시 불변.
 */
export function resolveEyeGrade(opts: {
  stage: BenchmarkStage;
  pnlPct: number;
  percentile: number | null;
}): ResolvedEyeGrade {
  const { stage, pnlPct, percentile } = opts;
  const abs = eyeGrade(pnlPct);
  if (stage === 'seed' || percentile == null) {
    return { grade: abs, stage, basis: '수익률 기준', percentile: null, byPercentile: false };
  }
  const pct = eyeGradeByPercentile(percentile);
  if (stage === 'mature') {
    return { grade: pct, stage, basis: `솟다 유저 상위 ${percentile}%`, percentile, byPercentile: true };
  }
  // grow: 절대·백분위 중 높은 단계 채택.
  // 🔴 2026-09-09: 종전엔 어느 쪽이 채택되든 basis를 '솟다 유저 대비'로 고정 반환했다.
  //   절대 수익률로 등급이 정해진 경우에도 화면이 "다른 유저 대비로 매긴다"고 설명해
  //   **실제 산정 기준과 안내 문구가 어긋났다**(외부 감사 프로브: pnlPct=30·percentile=80이면
  //   절대 기준으로 '전설'인데 basis는 유저 대비라고 말했다). 채택된 쪽을 그대로 말한다.
  const usePercentile = GRADE_RANK[pct.key] >= GRADE_RANK[abs.key];
  const grade = usePercentile ? pct : abs;
  return {
    grade, stage, percentile, byPercentile: usePercentile,
    basis: usePercentile ? `솟다 유저 상위 ${percentile}%` : '수익률 기준',
  };
}

/**
 * 손익 색 — 한국 증시 관례(상승 빨강 / 하락 파랑). 0은 중립(--cs-mute).
 * P2 §20: SimScreen·StockDetail·PortfolioAnalysis가 각자 정의하던 3중 복제를
 * 단일 출처로 통합. 0 처리가 화면마다 달랐던 불일치(SimScreen=중립 vs 나머지=rise) 해소 →
 * 매수 직후 +0.00%가 빨강으로 안 보이게 '중립' 규칙으로 통일.
 * 잉크(다크그린) 표면 위에서는 대비를 위해 별도 *-on-ink 토큰을 직접 쓴다(이 함수 미사용).
 */
export const pnlColor = (n: number | null | undefined): string =>
  ((n ?? 0) > 0 ? 'var(--cs-rise)' : (n ?? 0) < 0 ? 'var(--cs-fall)' : 'var(--cs-mute)');
